import { useEffect, useRef, useState } from "react";
import { useDrag } from "react-dnd";
import { useSelector } from "react-redux";
import ChevronDownIcon from "../../../../../../components/icons/ChevronDownIcon";
import dragImage from "../../../../../../assets/images/svgs/drag-handle-dots-1-svgrepo-com.svg";
import CopyTextBtn from "../../../../../../components/copyTextBtn/CopyTextBtn";
import Loader from "../../../../../../components/loader/Loader";
import TextWrapper from "../../../../../../components/textWrapper/TextWrapper";
import { defaultProfileImage } from "../../../../../../constants/defaultUserImage";
import styles from "./oneSquad.module.css";

const ITEM_TYPE = "player";

const roleLabelMap = {
  captain: "صاحب السكواد",
  coLeader: "مساعد السكواد",
  member: "عضو سكواد",
};

const OneSquad = ({
  squad,
  tableId,
  squadIndex,
  isSelected,
  isChecked = false,
  isCheckUpdating = false,
  showMembers: showMembersProp,
  onToggleMembers,
  onToggleChecked,
}) => {
  const [internalShowMembers, setInternalShowMembers] = useState(true);
  const { rolePermissions } = useSelector((state) => state.user);
  const canUpdateTables = rolePermissions.includes("update-tables");

  const dragHandleRef = useRef(null);
  const squadRef = useRef(null);

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ITEM_TYPE,
    item: {
      kind: "squad",
      squadId: squad?.squadId || squad?._id,
      tableId,
    },
    canDrag: () => rolePermissions.includes("update-tables"),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    if (dragHandleRef.current) {
      drag(dragHandleRef);
    }

    if (squadRef.current) {
      dragPreview(squadRef);
    }
  }, [drag, dragPreview]);

  const members = Array.isArray(squad?.members) ? squad.members : [];
  const showMembers =
    typeof showMembersProp === "boolean" ? showMembersProp : internalShowMembers;

  const handleToggleMembers = () => {
    if (typeof onToggleMembers === "function") {
      onToggleMembers();
      return;
    }

    setInternalShowMembers((prev) => !prev);
  };

  return (
    <div
      className={`${styles.container} ${
        isSelected ? styles.highlightedPlayer : ""
      } ${isDragging ? styles.dragging : ""} ${
        isChecked ? styles.checkedPlayer : ""
      }`}
      ref={squadRef}
      id={`player-${squad?.rowId || squad?._id}`}
      data-player-id={squad?.rowId || squad?._id}
      data-player-kind="squad"
      data-table-id={tableId}
    >
      <div className={styles.header}>
        <div className={styles.rightSide}>
          <img
            ref={dragHandleRef}
            src={dragImage}
            alt="drag"
            className={styles.dragHandle}
            data-no-select-box
          />

          <div className={styles.indexWrapper}>
            <TextWrapper textStyle="textMid">{squadIndex + 1}</TextWrapper>
          </div>

          <div className={styles.imageWrapper}>
            {squad?.userImage?.path ? (
              <img src={squad.userImage.path} alt="squad" />
            ) : (
              <img src={defaultProfileImage} alt="squad" />
            )}
          </div>

          <div className={styles.nameWrapper}>
            <div className={styles.nameRow}>
              <TextWrapper textStyle="textSmall">{squad?.name}</TextWrapper>
              {squad?.winsCount > 0 && (
                <span className={styles.winsBadge}>
                  {squad.winsCount} فوز
                </span>
              )}
            </div>
            <div className={styles.slugWrapper}>
              <TextWrapper textStyle="textXSmall">
                {squad?.squadSlug ? `(${squad.squadSlug})` : ""}
              </TextWrapper>
            </div>
          </div>
        </div>

        <div className={styles.leftSide}>
          <div
            className={styles.checkControl}
            data-no-select-box
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            {isCheckUpdating ? (
              <Loader size={18} />
            ) : (
              <input
                type="checkbox"
                checked={isChecked}
                title={isChecked ? "الغاء تحديد الفريق" : "تحديد الفريق"}
                disabled={!canUpdateTables}
                onChange={(event) => onToggleChecked?.(event.target.checked)}
              />
            )}
          </div>
          <TextWrapper textStyle="textSmall">
            الاعضاء: {squad?.membersCount || members.length}
          </TextWrapper>
          <button
            className={styles.toggleBtn}
            onClick={handleToggleMembers}
            data-no-select-box
            type="button"
            title={showMembers ? "اخفاء الاعضاء" : "عرض الاعضاء"}
          >
            <ChevronDownIcon
              className={`${styles.toggleArrow} ${
                showMembers ? styles.arrowExpanded : styles.arrowCollapsed
              }`}
            />
          </button>
        </div>
      </div>

      {showMembers && (
        <div className={styles.membersWrapper} data-no-select-box>
          {members.map((member) => (
            <div className={styles.memberRow} key={member?._id || member?.userId}>
              <div className={styles.memberIdentity}>
                <div className={styles.memberImage}>
                  {member?.userImage?.path ? (
                    <img src={member.userImage.path} alt="member" />
                  ) : (
                    <img src={defaultProfileImage} alt="member" />
                  )}
                </div>
                <div className={styles.memberTextWrapper}>
                  <div className={styles.memberPrimaryLine}>
                    <span className={styles.memberUserName}>
                      {member?.userName || member?.username || member?.name || "-"}
                    </span>
                    {member?.role && <span className={styles.memberSeparator}>-</span>}
                    {member?.role && (
                      <span className={styles.memberRole}>
                        {roleLabelMap[member.role] || member.role}
                      </span>
                    )}
                  </div>
                  <span className={styles.memberGameName}>{member?.gameName || "-"}</span>
                </div>
              </div>

              <div className={styles.memberMeta}>
                <span className={styles.memberGameId}>{member?.gameId || "-"}</span>
                <CopyTextBtn getText={() => member?.gameId || ""} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OneSquad;
